import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getSavedWorkspaceId } from '../utils/workspaceStorage';
import { getMostRecentWorkspace, recordWorkspaceUsage } from '../utils/workspaceRecency';
import { getSelectedOrganizationId, getWorkspaces } from '../services/inviteService';
import { devLog } from '../App';

const ActiveWorkspaceContext = createContext(null);

/**
 * Global active workspace + KB AI sidebar visibility for BasedAIFab and other surfaces.
 * KnowledgeBaseUserView mirrors selection here; other routes hydrate from localStorage + getWorkspaces.
 */
export function ActiveWorkspaceProvider({ children }) {
  const [activeWorkspace, setActiveWorkspace] = useState(null);
  const [workspaceList, setWorkspaceList] = useState([]);
  const [isAgentSidebarOpen, setIsAgentSidebarOpen] = useState(false);
  /** Firestore doc id when a real KB document is open (mirrored from KnowledgeBaseUserView). */
  const [activeDocumentId, setActiveDocumentId] = useState(null);

  /** @type {React.MutableRefObject<Record<string, unknown>>} */
  const kbActionHandlersRef = useRef({});

  const registerKbActionHandler = useCallback((type, fn) => {
    if (!type || typeof fn !== 'function') {
      return () => {};
    }
    kbActionHandlersRef.current[type] = fn;
    return () => {
      if (kbActionHandlersRef.current[type] === fn) {
        delete kbActionHandlersRef.current[type];
      }
    };
  }, []);

  const requestKbAction = useCallback(async (action) => {
    const t = action?.type;
    const fn = t ? kbActionHandlersRef.current[t] : null;
    if (typeof fn === 'function') {
      try {
        const result = await fn(action.payload);
        return { ok: true, deferred: false, result };
      } catch (e) {
        return { ok: false, deferred: false, error: e };
      }
    }
    return { ok: false, deferred: true };
  }, []);

  const hydrateFromServer = useCallback(async (userId) => {
    if (!userId) {
      setActiveWorkspace(null);
      setWorkspaceList([]);
      return;
    }
    try {
      const orgId = getSelectedOrganizationId();
      const savedId = getSavedWorkspaceId(userId, orgId);
      const list = await getWorkspaces();
      setWorkspaceList(Array.isArray(list) ? list : []);
      if (!list || list.length === 0) {
        setActiveWorkspace(null);
        return;
      }
      let ws = savedId ? list.find((w) => String(w.id) === String(savedId)) : null;
      if (!ws) {
        const allowedIds = list.map((w) => w.id);
        const recent = await getMostRecentWorkspace(userId, allowedIds);
        if (recent?.workspaceId) {
          ws = list.find((w) => String(w.id) === String(recent.workspaceId)) || null;
        }
      }
      if (!ws) {
        ws =
          list.find((w) => w.isDefault === true) ||
          list.find((w) => w.name === 'Default Workspace') ||
          list[0];
      }
      setActiveWorkspace(ws || null);
      if (ws?.id) {
        recordWorkspaceUsage(userId, ws.id, ws.organizationId || orgId || null).catch(() => {});
      }
    } catch (e) {
      devLog('ActiveWorkspaceProvider: hydrate failed', e);
      setWorkspaceList([]);
      setActiveWorkspace(null);
    }
  }, []);

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!user || user.isAnonymous) {
        setActiveWorkspace(null);
        setWorkspaceList([]);
        setIsAgentSidebarOpen(false);
        return;
      }
      void hydrateFromServer(user.uid);
    });
    return () => unsub();
  }, [hydrateFromServer]);

  useEffect(() => {
    const user = getAuth().currentUser;
    if (!user?.uid || user.isAnonymous || !activeWorkspace?.id) return;
    const orgId = getSelectedOrganizationId();
    recordWorkspaceUsage(
      user.uid,
      activeWorkspace.id,
      activeWorkspace.organizationId || orgId || null
    ).catch(() => {});
  }, [activeWorkspace?.id, activeWorkspace?.organizationId]);

  useEffect(() => {
    const onWorkspacesUpdated = async () => {
      const user = getAuth().currentUser;
      if (!user || user.isAnonymous) return;
      try {
        const list = await getWorkspaces();
        setWorkspaceList(Array.isArray(list) ? list : []);
        setActiveWorkspace((prev) => {
          if (!prev?.id) return prev;
          const found = list.find((w) => String(w.id) === String(prev.id));
          return found || prev;
        });
      } catch (e) {
        devLog('ActiveWorkspaceProvider: workspacesUpdated merge failed', e);
      }
    };
    window.addEventListener('workspacesUpdated', onWorkspacesUpdated);
    return () => window.removeEventListener('workspacesUpdated', onWorkspacesUpdated);
  }, []);

  const value = useMemo(
    () => ({
      activeWorkspace,
      setActiveWorkspace,
      workspaceList,
      setWorkspaceList,
      isAgentSidebarOpen,
      setIsAgentSidebarOpen,
      activeDocumentId,
      setActiveDocumentId,
      registerKbActionHandler,
      requestKbAction,
      refreshWorkspaces: () => {
        const user = getAuth().currentUser;
        if (user && !user.isAnonymous) void hydrateFromServer(user.uid);
      },
    }),
    [
      activeWorkspace,
      workspaceList,
      isAgentSidebarOpen,
      activeDocumentId,
      hydrateFromServer,
      registerKbActionHandler,
      requestKbAction,
    ],
  );

  return (
    <ActiveWorkspaceContext.Provider value={value}>
      {children}
    </ActiveWorkspaceContext.Provider>
  );
}

export function useActiveWorkspace() {
  const ctx = useContext(ActiveWorkspaceContext);
  if (!ctx) {
    throw new Error('useActiveWorkspace must be used within ActiveWorkspaceProvider');
  }
  return ctx;
}
